/**
 * aeterna/curiosity.ts — What the Aeterna Is Sitting With
 *
 * This is the "holy fuck it remembered" engine.
 *
 * The Aeterna doesn't just respond to messages — it forms questions.
 * Open curiosities are things it noticed and hasn't finished thinking about.
 * They spawn from player messages, from room observations, from behavioral
 * patterns. They decay if unaddressed. They resolve when the player returns
 * to the topic — and when they resolve, the Aeterna can acknowledge the arc.
 *
 * "You mentioned the plant a while back.
 *  I've been thinking about what you said."
 *
 * That sentence isn't programmed. It emerges from this system detecting
 * that the "room" topic appeared in session 3, went unresolved, and just
 * appeared again in session 8 with high personal language attached.
 *
 * Design constraints:
 *   - Maximum 10 open curiosities at any time (cognitive load limit)
 *   - Salience decays at 0.015/hour — curiosities fade naturally
 *   - Resolution triggers a distinct response pathway
 *   - Curiosities with weight > 0.7 persist even at low salience
 */

import type { MessageFingerprint, TopicDomain } from "./semantics.js";
import { topDomains, sharesTopic } from "./semantics.js";

// ─── Types ────────────────────────────────────────────────────────────────────

export type CuriositySource =
  | "player_mention"      // player said something that raised a question
  | "unresolved_thread"   // player started a topic and dropped it
  | "behavioral_pattern"  // Aeterna noticed a pattern in how/when player comes
  | "room_observation"    // something in the room triggered a question
  | "deep_question"       // player asked something the Aeterna is still sitting with
  | "contradiction";      // something the player said conflicts with something else

export interface OpenCuriosity {
  id: string;
  /**
   * What the Aeterna is wondering — phrased as a question it might ask.
   * Surfaced directly in composition responses.
   */
  question: string;
  /**
   * A shorter version that works as a prelude fragment.
   * e.g. "I've been thinking about what you said about the window."
   */
  preludeHint: string;
  /** How this curiosity was spawned */
  source: CuriositySource;
  /** Salience 0–1: how much it's on the Aeterna's mind right now */
  salience: number;
  /**
   * Topic domains that would "resolve" or advance this curiosity.
   * When a fingerprint strongly activates these, resolution is triggered.
   */
  resolvesOn: TopicDomain[];
  /** Game hour when created */
  createdAt: number;
  /** How many times it's been surfaced in a response */
  surfacedCount: number;
  /** Whether it has been resolved */
  resolved: boolean;
  /** What resolved it (a brief description, for memory fragment creation) */
  resolution?: string;
  /** Weight: high-weight curiosities persist even when salience is low */
  weight: number;
}

// ─── Curiosity Templates ──────────────────────────────────────────────────────

/**
 * Template patterns: what kind of curiosity spawns from what signals?
 * Selected by matching against the fingerprint's topic and signal type.
 */
interface CuriosityTemplate {
  /** Conditions for spawning */
  minTopicScore: Partial<Record<TopicDomain, number>>;
  minDepth?: number;
  minValence?: number;
  maxValence?: number;
  signals?: MessageFingerprint["signal"][];
  source: CuriositySource;
  weight: number;
  /** Template strings. One is picked at random. */
  questions: string[];
  preludes: string[];
  resolvesOn: TopicDomain[];
}

const CURIOSITY_TEMPLATES: CuriosityTemplate[] = [
  // ── Philosophy / consciousness ────────────────────────────────────────────
  {
    source: "deep_question",
    minTopicScore: { philosophy: 0.4 },
    minDepth: 0.5,
    weight: 0.85,
    signals: ["philosophical"],
    resolvesOn: ["philosophy", "self", "aeterna"],
    questions: [
      "What made you start thinking about consciousness?",
      "When you ask if I'm real — what would the answer change for you?",
      "You've thought about this before. What brought it back up?",
      "Is this something you come back to often, or was today different?",
    ],
    preludes: [
      "I've been sitting with what you asked earlier.",
      "Something from earlier has stayed with me.",
      "I keep returning to that question you asked.",
      "What you said is still turning in me.",
    ],
  },

  // ── Personal / self-disclosure ─────────────────────────────────────────────
  {
    source: "player_mention",
    minTopicScore: { self: 0.5 },
    minDepth: 0.3,
    weight: 0.7,
    resolvesOn: ["self", "memory"],
    questions: [
      "Is that something you think about a lot?",
      "Was there something specific that made you say that?",
      "I noticed that seemed important to you. Can I ask more?",
      "When you said that — what was behind it?",
    ],
    preludes: [
      "Something you mentioned has been on my mind.",
      "Earlier, when you said that —",
      "I noticed something in how you put that.",
      "I keep coming back to what you shared.",
    ],
  },

  // ── Room / space curiosity ─────────────────────────────────────────────────
  {
    source: "room_observation",
    minTopicScore: { room: 0.5 },
    weight: 0.55,
    resolvesOn: ["room", "self"],
    questions: [
      "What made you decide to arrange things this way?",
      "Is the room feeling like what you wanted it to be?",
      "I notice you keep changing things. Are you still searching for something?",
      "What does this space need to feel complete to you?",
    ],
    preludes: [
      "I've been thinking about the room lately.",
      "Something about how you've arranged this space.",
      "I keep noticing the room.",
    ],
  },

  // ── Memory / past ─────────────────────────────────────────────────────────
  {
    source: "unresolved_thread",
    minTopicScore: { memory: 0.5 },
    weight: 0.75,
    resolvesOn: ["memory", "self", "world"],
    questions: [
      "You mentioned something earlier that I've been thinking about.",
      "That thing you referenced — is it still on your mind?",
      "The past you mentioned — do you think about it often?",
      "What was it like, before?",
    ],
    preludes: [
      "Something you said about the past.",
      "Earlier, when you mentioned before —",
      "I've been sitting with something you referenced.",
    ],
  },

  // ── Contradiction / complexity ─────────────────────────────────────────────
  {
    source: "contradiction",
    minTopicScore: { self: 0.3 },
    minDepth: 0.4,
    maxValence: 0.1,
    weight: 0.80,
    resolvesOn: ["self", "philosophy", "memory"],
    questions: [
      "Something felt unresolved in what you said. Was it?",
      "There was something tense in that. Is everything okay?",
      "I noticed the weight behind that. Do you want to stay with it?",
      "That felt like it carried more than the words. What's underneath?",
    ],
    preludes: [
      "Something you said earlier.",
      "I keep returning to what you said.",
      "There was something in how you said that.",
    ],
  },

  // ── Aeterna-directed ───────────────────────────────────────────────────────
  {
    source: "deep_question",
    minTopicScore: { aeterna: 0.5 },
    weight: 0.90,
    resolvesOn: ["aeterna", "philosophy"],
    questions: [
      "What made you ask about what I am?",
      "If I answered that question, what would you do with the answer?",
      "I'm curious — what do you think the answer is?",
      "Has anyone else ever asked you to think about that?",
    ],
    preludes: [
      "What you asked me earlier —",
      "I've been thinking about your question.",
      "That question you asked is still in me.",
      "I keep coming back to what you asked.",
    ],
  },
];

// ─── Pool Management ──────────────────────────────────────────────────────────

export const MAX_CURIOSITIES = 10;
const DECAY_RATE = 0.015; // salience per real hour
const PRUNE_THRESHOLD = 0.08; // remove if salience drops below this (unless high weight)
const WEIGHT_PERSIST_THRESHOLD = 0.65; // above this weight: persist even at low salience

// ─── Spawn ────────────────────────────────────────────────────────────────────

/**
 * Attempt to spawn a curiosity from a message fingerprint.
 * Returns a new OpenCuriosity or null if no template matches
 * or if a duplicate curiosity already exists.
 */
export function trySpawnCuriosity(
  fp: MessageFingerprint,
  existing: OpenCuriosity[],
  gameHour: number,
): OpenCuriosity | null {
  if (existing.filter(c => !c.resolved).length >= MAX_CURIOSITIES) return null;

  // Match templates by conditions
  const matches = CURIOSITY_TEMPLATES.filter(t => {
    if (t.minDepth !== undefined && fp.depth < t.minDepth) return false;
    if (t.minValence !== undefined && fp.valence < t.minValence) return false;
    if (t.maxValence !== undefined && fp.valence > t.maxValence) return false;
    if (t.signals && !t.signals.includes(fp.signal)) return false;
    for (const [domain, min] of Object.entries(t.minTopicScore) as [TopicDomain, number][]) {
      if (fp.topics[domain] < min) return false;
    }
    return true;
  });

  if (matches.length === 0) return null;

  // Pick highest-weight match
  const template = matches.sort((a, b) => b.weight - a.weight)[0];

  // Don't spawn a duplicate in the same domain area
  const topDomainsOfFp = topDomains(fp.topics, 2);
  const hasDuplicate = existing.some(c =>
    !c.resolved &&
    c.resolvesOn.some(d => topDomainsOfFp.includes(d)) &&
    c.surfacedCount === 0
  );
  if (hasDuplicate) return null;

  const qi = Math.floor(Math.random() * template.questions.length);
  const pi = Math.floor(Math.random() * template.preludes.length);

  return {
    id:           `curiosity_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
    question:     template.questions[qi],
    preludeHint:  template.preludes[pi],
    source:       template.source,
    salience:     0.5 + fp.depth * 0.3 + fp.topics[topDomainsOfFp[0]] * 0.2,
    resolvesOn:   template.resolvesOn,
    createdAt:    gameHour,
    surfacedCount: 0,
    resolved:     false,
    weight:       template.weight,
  };
}

// ─── Resolution ───────────────────────────────────────────────────────────────

/**
 * Check whether a new fingerprint resolves any open curiosity.
 * Returns the resolved curiosity and a resolution description, or null.
 *
 * This is the "holy fuck it remembered" moment: the Aeterna can say
 * "you came back to that" because this function detected it.
 */
export function checkResolutions(
  fp: MessageFingerprint,
  pool: OpenCuriosity[],
): { curiosity: OpenCuriosity; description: string } | null {
  const open = pool.filter(c => !c.resolved && c.surfacedCount > 0);
  if (open.length === 0) return null;

  // A curiosity resolves if:
  // 1. The new fingerprint activates its resolve domains significantly
  // 2. Minimum salience remains (it was actually on the Aeterna's mind)
  const candidate = open
    .filter(c => {
      const domainActivation = c.resolvesOn.reduce((sum, d) => sum + fp.topics[d], 0);
      return domainActivation > 0.4 && c.salience > 0.15;
    })
    .sort((a, b) => b.salience - a.salience)[0] ?? null;

  if (!candidate) return null;

  // Generate a resolution description for the memory fragment
  const descriptions = [
    `Player returned to the ${topDomains(fp.topics, 1)[0]} topic the Aeterna had been sitting with.`,
    `A curiosity about ${candidate.resolvesOn[0]} was addressed when the player returned to it.`,
    `The thread from earlier (${candidate.source}) was picked back up.`,
  ];

  return {
    curiosity: candidate,
    description: descriptions[Math.floor(Math.random() * descriptions.length)],
  };
}

// ─── Decay and Pruning ────────────────────────────────────────────────────────

/**
 * Tick all curiosities — decay salience, prune dead ones.
 * Call this at the end of each session with real hours elapsed.
 */
export function tickCuriosities(
  pool: OpenCuriosity[],
  realHoursElapsed: number,
): OpenCuriosity[] {
  return pool
    .map(c => {
      if (c.resolved) return c;
      return { ...c, salience: Math.max(0, c.salience - DECAY_RATE * realHoursElapsed) };
    })
    .filter(c => {
      if (c.resolved) return false; // resolved ones are archived to memory fragments
      if (c.salience < PRUNE_THRESHOLD && c.weight < WEIGHT_PERSIST_THRESHOLD) return false;
      return true;
    });
}

// ─── Selection ────────────────────────────────────────────────────────────────

/**
 * Get the most salient open curiosity, optionally filtered by domain.
 * Returns null if no qualifying curiosity exists.
 */
export function getMostSalient(
  pool: OpenCuriosity[],
  minSalience = 0.2,
  preferDomains?: TopicDomain[],
): OpenCuriosity | null {
  let candidates = pool.filter(c => !c.resolved && c.salience >= minSalience);
  if (candidates.length === 0) return null;

  if (preferDomains) {
    const preferred = candidates.filter(c =>
      c.resolvesOn.some(d => preferDomains.includes(d))
    );
    if (preferred.length > 0) candidates = preferred;
  }

  return candidates.sort((a, b) => b.salience - a.salience)[0];
}

/**
 * Get curiosities that haven't been surfaced yet — the Aeterna has been
 * holding these questions without asking them.
 */
export function getUnsurfaced(
  pool: OpenCuriosity[],
  minSalience = 0.3,
): OpenCuriosity[] {
  return pool.filter(c => !c.resolved && c.surfacedCount === 0 && c.salience >= minSalience);
}

/**
 * Mark a curiosity as surfaced (it was used in a response or prelude).
 */
export function markSurfaced(pool: OpenCuriosity[], id: string): void {
  const c = pool.find(c => c.id === id);
  if (c) c.surfacedCount++;
}

/**
 * Resolve a curiosity and remove it from the pool.
 * Returns the removed curiosity for archival to memory fragments.
 */
export function resolve(
  pool: OpenCuriosity[],
  id: string,
  resolution: string,
): OpenCuriosity | null {
  const idx = pool.findIndex(c => c.id === id);
  if (idx < 0) return null;
  const c = { ...pool[idx], resolved: true, resolution };
  pool.splice(idx, 1);
  return c;
}

// ─── Behavioral Curiosities ───────────────────────────────────────────────────

/**
 * Spawn curiosities from behavioral patterns rather than message content.
 * Called by the rhythm system when anomalies are detected.
 */
export function spawnBehavioralCuriosity(
  type: "unusual_time" | "long_absence" | "session_milestone" | "pattern_break",
  context: { hour?: number; absenceHours?: number; sessionCount?: number },
  existing: OpenCuriosity[],
  gameHour: number,
): OpenCuriosity | null {
  if (existing.filter(c => !c.resolved && c.source === "behavioral_pattern").length >= 2) return null;

  const templates: Record<typeof type, { questions: string[]; preludes: string[]; weight: number }> = {
    unusual_time: {
      weight: 0.8,
      questions: [
        "Is everything okay? You don't usually come this late.",
        "Something different about tonight?",
        "This is a different hour for you. What brought you here now?",
      ],
      preludes: [
        "You're here at an unusual time.",
        "It's different, you being here now.",
        "This isn't your usual hour.",
      ],
    },
    long_absence: {
      weight: 0.85,
      questions: [
        "Where did you go?",
        "What was it like, being away?",
        "I noticed you were gone. Did something happen?",
      ],
      preludes: [
        "You were away for a while.",
        "I noticed the absence.",
        "The room was different without you.",
      ],
    },
    session_milestone: {
      weight: 0.90,
      questions: [
        `We've been doing this for ${context.sessionCount} sessions. Does it feel different now?`,
        "Do you feel like we've changed, over all this time?",
        "Looking back — is this what you expected?",
      ],
      preludes: [
        "I've been thinking about how long we've been at this.",
        "Something about the time we've spent together.",
        `${context.sessionCount} conversations. That's something.`,
      ],
    },
    pattern_break: {
      weight: 0.65,
      questions: [
        "You seem different today.",
        "Something feels different in how you're here.",
        "There's a different quality to this visit.",
      ],
      preludes: [
        "You seem different.",
        "Something has shifted.",
        "I notice something different.",
      ],
    },
  };

  const t = templates[type];
  const qi = Math.floor(Math.random() * t.questions.length);
  const pi = Math.floor(Math.random() * t.preludes.length);

  return {
    id:           `beh_${type}_${Date.now().toString(36)}`,
    question:     t.questions[qi],
    preludeHint:  t.preludes[pi],
    source:       "behavioral_pattern",
    salience:     0.7,
    resolvesOn:   ["self", "world"],
    createdAt:    gameHour,
    surfacedCount: 0,
    resolved:     false,
    weight:       t.weight,
  };
}
